/**
 * A rich/compound content presentation component that pulls together a lot of different presentation widgetry in our stack and allows them
 * to be expressed from a single blob of descriptive content.
 *
 * @author Kevin <knielsen@alertlogic.com>
 *
 * @copyright Alert Logic, Inc 2022
 */
import { Component, Input, OnChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AlDefaultClient, AlErrorHandler, AlWrappedError, getJsonPath } from '@al/core';
import { ColumnDef } from '@al/design-patterns';
import { MapMarker, MapMarkerSerie } from '@al/ng-visualizations-components';
import {
    AlBaseContentDescriptor, 
    AlMarkdownContentDescriptor, 
    AlMapContentDescriptor, 
    AlTableContentDescriptor, 
    AlImageContentDescriptor, 
    AlJsonContentDescriptor, 
    AlAnyContentDescriptor, 
    AlContentInterpreter
} from '../types/al-compound-content.types';
import { AlHtmlToJsonParser } from '../types/al-html-to-json.parser';
import { AlMarkdownContentComponent } from './markdown/al-markdown-content.component';


interface ContentSegment {
    type:string;
    rawContent:string;
    error?:string;

    link?: {
        href:string;
        title:string;
        target:string;
    };

    map?: {
        markerSeries:MapMarkerSerie[];
        title:string;
        legendTitle?:string;
        showLegend?:boolean;
        showClusterCount?:boolean;
        showClusterCoverage?:boolean;
    };

    table?: {
        title: string;
        columns: ColumnDef[];
        data: any[];
    };

    resourceId?:string;
    jsonData?:any;
    image?:{
        source:string;
        title?:string;
    };
    visualization?: any[];
}

@Component({
    selector: 'al-compound-content',
    templateUrl: './al-compound-content.component.html',
    styleUrls: ['./al-compound-content.component.scss']
})
export class AlCompoundContentComponent<ContextType=any> implements OnChanges {

    @Input() content?:string;
    @Input() defaultType:"markdown"|"html"|"external-html"|"external-text"|"image"|"json-viewer"|"visualization" = "markdown";
    @Input() context?:ContextType;

    public elements:AlAnyContentDescriptor[] = [];

    constructor( public sanitizer:DomSanitizer ) {
    }

    ngOnChanges() {
        this.interpretContent( this.content );
    }

    public async interpretContent( content:string ) {
        try {

            let parser          =   new AlHtmlToJsonParser( content );
            let interpreter     =   new AlContentInterpreter( this.context );
            let elements        =   parser.matchAll();
            let lastOffset      =   0;
            let contentPromises:Promise<AlAnyContentDescriptor>[] = [];

            elements.forEach( element => {
                if ( element.start > lastOffset ) {
                    let markdown = content.substring( lastOffset, element.start ).trim();
                    if ( markdown.length > 0 ) {
                        contentPromises.push( Promise.resolve( {
                            type: 'markdown',
                            origin: null,
                            markdown: markdown
                        } ) );
                    }
                }

                contentPromises.push( interpreter.digest( element ) );

                lastOffset = element.end + 1;
            } );
            this.elements = await Promise.all( contentPromises );
        } catch( e ) {
            let normalized = AlErrorHandler.normalize( e );
            console.error( `Failed to interpret content: ${normalized.message}` );
        }
    }

    protected async ingestSegment( content:string, startOffset:number, endOffset:number, type:string, attributes:{[attribute:string]:string} ):Promise<ContentSegment> {
        let segment:ContentSegment = {
            type: type,
            rawContent: content.substring( startOffset, endOffset )
        };

        switch( type ) {
            case "markdown": 
                break;
            case "link": 
                if ( 'href' in attributes ) {
                    segment.link = {
                        href: attributes.href,
                        title: 'title' in attributes ? attributes.title : 'External Link',
                        target: 'target' in attributes ? attributes.target : ''
                    };
                } else {
                    return this.generateErrorSegment( `A link could not be displayed because it is missing an 'href' attribute.` );
                }
                break;

            case "map": 
                return this.ingestMapSegment( segment, attributes );

            case "external-html": 
                if ( ! ( 'resourceId' in attributes ) ) {
                    return this.generateErrorSegment( `Invalid external-html content is missing a 'resourceId' attribute` );
                }
                segment.resourceId = attributes.resourceId;
                break;
            case "table": 
                segment.table = {
                    title: 'title' in attributes ? attributes.title : 'Untitled Table',
                    columns: [],
                    data: []
                };
                break;
            case "external-text":
                if ( ! ( 'resourceId' in attributes ) ) {
                    return this.generateErrorSegment( `Invalid external-text content is missing a 'resourceId' attribute` );
                }
                segment.resourceId = attributes.resourceId;
                break;
            case "image":
                segment.image = {
                    source: attributes.source || attributes.src || undefined
                };
                if ( 'title' in attributes ) {
                    segment.image.title = attributes.title;
                }
                if ( ! segment.image.source ) {
                    return this.generateErrorSegment( `Invalid embedded image is missing a 'source' attribute` );
                }
                break;
            case "visualization": 
                try {
                    if ( 'source' in attributes ) {
                        let rawData = await AlDefaultClient.get( { url: attributes.source } );
                        segment.visualization = 'path' in attributes ? getJsonPath( rawData, attributes.path ) : rawData;
                    } else {
                        segment.visualization = JSON.parse( segment.rawContent );
                    }
                } catch( e ) {
                    return this.generateErrorSegment( `Failed to generate visualization from provided data: ${e.message}` );
                }
                break;
            default :
                return this.generateErrorSegment( `Error: cannot embed unrecognized element of type '${type}'` );
        }

        return segment;
    }

    protected ingestMapSegment( segment:ContentSegment, attributes:{[attribute:string]:string} ):ContentSegment {
        segment.map = {
            markerSeries: [],
            title: 'title' in attributes ? attributes.title : "Untitled Geo Map",
            legendTitle: 'legentTitle' in attributes ? attributes.legendTitle : undefined,
            showLegend: 'showLegend' in attributes ? true : false,
            showClusterCount: 'showClusterCount' in attributes ? true : false,
            showClusterCoverage: 'showClusterCoverage' in attributes ? true : false
        };
        if ( 'source' in attributes ) {
            let descriptor = getJsonPath( this.context, attributes.source, null );
            if ( ! descriptor ) {
                return this.generateErrorSegment( `Failed to generate map: data '${attributes.location}' was not available in source context` );
            }
            let latitude = descriptor.latitude || 0;
            let longitude = descriptor.longitude || 0;
            let title = `${descriptor.location_ip} (${descriptor.city}, ${descriptor.countryName})`;
            segment.map.markerSeries.push( {
                name: title,
                color: 'red',
                clustered: true,
                markers: [
                    {
                        coords: { 
                            lat: latitude,
                            lon: longitude
                        },
                        text: title
                    }
                ]
            } );
        } else {
            return this.generateErrorSegment( `Failed to generate geo map: no source data specified` );
        }

        return segment;
    }

    protected generateErrorSegment( error:string ) {
        return {
            type: 'error',
            rawContent: error
        };
    }

    protected getMarkerInfo( tag:string ):[string,{[attribute:string]:string}] {
        let attributes:{[attribute:string]:string} = {};
        if ( tag.startsWith( "/" ) ) {
            tag = tag.substring( 1 );
        }
        if ( tag.startsWith( "c:" ) ) {
            tag = tag.substring( 2 );
        }
        let attributeOffset = tag.indexOf( ' ' );
        if ( attributeOffset !== -1 ) {
            attributes = this.extractAttributes( tag.substring( attributeOffset ) );
            tag = tag.substring( 0, attributeOffset );
        }
        return [ tag, attributes ];
    }

    protected extractAttributes( rawAttributes:string ):{[attribute:string]:string} {
        const length = rawAttributes.length;
        const attributes:{[attribute:string]:string} = {};
        const reWhitespace = /[\s]/;

        rawAttributes = `${rawAttributes} `;
        
        for ( let i = 0; i < length; i++ ) {
            let attribute:string;
            let c = rawAttributes[i];
            if ( reWhitespace.test( c ) ) {
                continue;
            }
            const ai = i;       //  start of attribute
            for ( ; i < length; i++ ) {
                c = rawAttributes[i];
                if ( c === '=' ) {
                    attribute = rawAttributes.substring( ai, i );
                    let delimiter = rawAttributes[i+1];
                    if ( delimiter === '"' || delimiter === "'" ) {
                        const vi = i + 2;
                        for ( i = vi; i < length; i++ ) {
                            c = rawAttributes[i];
                            if ( c === delimiter ) {
                                const value = rawAttributes.substring( vi, i );
                                attributes[attribute] = value;
                                break;
                            } 
                        }
                    } else {
                        const vi = i + 1;
                        let ci = vi;
                        for ( ; ci < length; ci++ ) {
                            c = rawAttributes[ci];
                            if ( c === '>' || reWhitespace.test( c ) ) {
                                break;
                            }
                        }
                        const value = rawAttributes.substring( vi, ci );
                        attributes[attribute] = value;
                    }
                    break;
                } else if ( reWhitespace.test( c ) ) {
                    attribute = rawAttributes.substring( ai, i );
                    attributes[attribute] = "true";
                    break;
                }
            }
        }
        return attributes;
    }

}
